import { Activity, Calendar, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { useState } from "react";

interface LogEntry {
  id: string;
  actorDetails: {
    full_name: string;
  };
  verboseDescription: string;
  timestamp: string;
}

interface TimelineProps {
  entries: LogEntry[];
}

export const Timeline = ({ entries }: TimelineProps) => {
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 3;

  const totalPages = Math.ceil(entries.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentEntries = entries.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setExpandedEntry(null);
    setCurrentPage(page);
  };

  return (
    <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50 hover:border-gray-700/50 transition-colors duration-200">
      <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <Clock className="h-5 w-5 text-blue-500" />
        Events
      </h2>
      <div className="relative pl-4">
        <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-blue-500/50 via-gray-800 to-transparent" />
        <div className="space-y-6">
          {currentEntries.map((event) => (
            <div key={event.id} className="relative group animate-fadeIn">
              <div className={`absolute -left-[0.4rem] w-2 h-2 rounded-full bg-blue-500 ring-4 ring-[#0F1629] group-hover:ring-blue-500/20 transition-all duration-200`} />
              <div className="bg-gray-800/30 rounded-lg p-4 group-hover:bg-gray-800/40 transition-colors duration-200 border border-transparent group-hover:border-blue-500/20">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 w-full">
                    <div className="p-2 rounded-lg bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors duration-200">
                      <Activity className="h-5 w-5 text-blue-500" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-white group-hover:text-blue-400 transition-colors duration-200">
                        {event.actorDetails.full_name}
                      </div>
                      <div className="text-sm text-gray-400 mt-1 relative">
                        {expandedEntry === event.id ? (
                          <>
                            {event.verboseDescription}
                            <button
                              onClick={() => setExpandedEntry(null)}
                              className="text-blue-500 hover:text-blue-400 text-xs ml-1 font-medium transition-colors duration-200"
                            >
                              Show less
                            </button>
                          </>
                        ) : (
                          <>
                            {event.verboseDescription.length > 100
                              ? `${event.verboseDescription.slice(0, 100)}...`
                              : event.verboseDescription}
                            {event.verboseDescription.length > 100 && (
                              <button
                                onClick={() => setExpandedEntry(event.id)}
                                className="text-blue-500 hover:text-blue-400 text-xs ml-1 font-medium transition-colors duration-200"
                              >
                                Read more
                              </button>
                            )}
                          </>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 mt-2 text-xs text-gray-500">
                        <Calendar className="h-3 w-3 text-gray-500" />
                        <span className="text-xs text-gray-500 group-hover:text-gray-400 transition-colors duration-200">
                          {new Date(event.timestamp).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm text-gray-400">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-1 rounded hover:bg-gray-800/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-1 rounded hover:bg-gray-800/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          <div className="text-xs">
            Showing {startIndex + 1}-{Math.min(endIndex, entries.length)} of {entries.length} items
          </div>
        </div>
      )}
    </div>
  );
};
